import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';

interface DevResetButtonProps {
  category: string;
  onReset: (category: string) => Promise<void>;
}

/**
 * Development-only floating reset button
 * Allows developers to reset game state and replay categories for testing/recording
 * Only visible in development mode and on hover
 */
export const DevResetButton: React.FC<DevResetButtonProps> = ({ category, onReset }) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  // Only render in development mode
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  const handleResetClick = () => {
    setShowConfirmDialog(true);
  };

  const handleConfirmReset = async () => {
    setIsResetting(true);
    try {
      await onReset(category);
      // Page will reload after successful reset
    } catch (error) {
      console.error('Failed to reset game:', error);
      alert('Failed to reset game. Check console for details.');
      setIsResetting(false);
      setShowConfirmDialog(false);
    }
  };

  const handleCancelReset = () => {
    setShowConfirmDialog(false);
  };

  return (
    <>
      {/* Floating Reset Button - Only visible on hover */}
      <div className="fixed bottom-6 right-6 z-50 group">
        <button
          onClick={handleResetClick}
          className="opacity-0 group-hover:opacity-100 transition-opacity duration-300
                     bg-yellow-500 hover:bg-yellow-600 text-white font-bold
                     py-3 px-4 rounded-full shadow-lg
                     flex items-center gap-2 text-sm"
          title="Dev: Reset current category game"
          disabled={isResetting}
        >
          <AlertTriangle size={18} />
          <span>DEV RESET</span>
        </button>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[100] flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="text-yellow-500" size={24} />
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Reset Game State?
              </h2>
            </div>

            <p className="text-gray-700 dark:text-gray-300 mb-6">
              This will reset your <strong>{category.toUpperCase()}</strong> game for today, allowing you to play again.
            </p>

            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded p-3 mb-6">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                <strong>Dev Only:</strong> This will clear:
              </p>
              <ul className="text-sm text-yellow-800 dark:text-yellow-200 list-disc list-inside mt-2">
                <li>localStorage game state</li>
                <li>Supabase game_plays record</li>
              </ul>
              <p className="text-sm text-yellow-800 dark:text-yellow-200 mt-2">
                The page will reload after reset.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleCancelReset}
                className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700
                           text-gray-800 dark:text-gray-200 rounded
                           hover:bg-gray-300 dark:hover:bg-gray-600
                           transition-colors"
                disabled={isResetting}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmReset}
                className="flex-1 px-4 py-2 bg-yellow-500 text-white rounded
                           hover:bg-yellow-600 transition-colors
                           disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isResetting}
              >
                {isResetting ? 'Resetting...' : 'Reset Game'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
